/**
 * Breakdown menu: multi-screen menu to select event+prop pair
 */
import {renameEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {propagateEvent} from 'common/util/dom';
import {PATH_SEPARATOR} from 'common/constants';
import {truncateMiddle} from 'common/util/string';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';

import {ChildMenu} from '../child-menu';
import type {EventItem} from '../events-menu/types';
import type {PropertySectionItem} from '../properties-menu/item-menu-types';
import type {EventsMenu} from '../events-menu';
import type {PropertiesMenu} from '../properties-menu';
import {MAX_LABEL_LENGTH} from '../constants';
import {template} from './template';

interface SelectedEventProperty {
  event: EventItem;
  property: PropertySectionItem;
}

interface SelectedEventPropertyItem {
  item: SelectedEventProperty;
}

export type SelectEventPropertyEvent = CustomEvent<SelectedEventPropertyItem>;

interface BreakdownMenuState {
  staged: SelectedEventProperty;
}

export class BreakdownMenu extends ChildMenu<BreakdownMenuState> {
  protected childMenu: EventsMenu | PropertiesMenu;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        staged: null,
      },

      helpers: {
        ...super.config.helpers,
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleSelectProperty: (ev) => {
          const property = ev.detail.item;
          this.dispatchEvent(
            new CustomEvent<SelectedEventPropertyItem>(`select`, {
              detail: {
                item: {
                  event: this.getSelectedEvent(),
                  property,
                },
              },
            }),
          );
        },
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected` || (attr === `open` && !this.attr(`open`))) {
      this.update({staged: this.attr(`selected`)});
    }
  }

  handleSelectEvent(event: EventItem) {
    this.update({staged: {event}, searchFilter: ``});
  }

  public getSelectedProperty() {
    return this.state.staged?.property || null;
  }

  getSelectedHoverTooltipData() {
    return this.childMenu?.getSelectedHoverTooltipData();
  }

  handleTooltipContextAction(ev: Event) {
    return this.childMenu?.handleTooltipContextAction(ev);
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged?.event;
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  getPrefixLabel() {
    let label: string = null;
    const selected = this.getSelectedEvent();
    const eventName = this.getSelectedEventName();
    let evName = renameEvent(eventName);

    if (evName) {
      /** @todo [pablo] find a better workaround for Insights using properties-menu for people mode */
      if (selected.value === `$all_people`) {
        evName = selected.label ?? eventName;
      }

      label = truncateMiddle(evName, MAX_LABEL_LENGTH) + PATH_SEPARATOR; // event name + right arrow
    }

    if (this.attr(`selected`)?.isBehavior) {
      label = `Event` + PATH_SEPARATOR + (label || ``);
    }

    return label;
  }

  selectedLabel() {
    let label = ``;
    const propName = getSelectedPropertyName({
      selected: this.getSelectedProperty(),
      hideSpecialDatePropertyUnit: this.attr(`hide-special-date-property-unit`),
    });

    if (!this.getSelectedEvent()?.value) {
      label = `Select event...`;
    } else {
      label = propName || `Select property...`;
    }

    return truncateMiddle(label, MAX_LABEL_LENGTH);
  }
}

export const BreakdownMenuEl = defineAndMakeJsxFactory(`mp-breakdown-menu`, BreakdownMenu);
export const BreakdownMenuReact = panel2react(`mp-breakdown-menu`, BreakdownMenu);
